import { Alert, AlertTitle, Box, IconButton, TextField, Tooltip } from "@mui/material"; // Add Button
import { DatePicker } from "@mui/x-date-pickers";
import { DateTime } from "luxon";
import { Controller, useFieldArray, UseFormReturn } from "react-hook-form";
import { DragDropContext, Draggable, Droppable, DroppableProps, DropResult, } from "react-beautiful-dnd";
import {
    DATE_WARNING,
    MAX_WAYPOINTS,
    NAME,
    NAME_TOOLTIP,
    START_DATE,
    START_DATE_TOOLTIP,
    WAYPOINT_WARNING,
} from "../../UserVisibleStrings";
import { useErrorProps, useMuiFieldRegister } from "./TripForm";
import React, { useEffect, useState } from "react";
import { dateSchemaToDate, dateToSchema, defaultWaypoint, RouteRequestFormData, } from "./TripFormTypeConversions";
import { DraggableWaypoint } from "./DraggableWaypoint";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { dragWaypoint, updateWaypoint } from "../../../features/riskViewer/riskViewerSlice";
import { useDispatch } from "react-redux";
import useIdentity from "../../../app/auth";
import { useForecastTime } from "../../../features/surface/surfaceSlice";
import { convertForecastTimeToDate } from "../../../app/forecastSurface";
import { setSelectedWaypointInputIndex } from "../../../features/Drawer/drawerSlice";
import { useUserViewOverride } from "../../../features/riskViewer/userViewOverrideSlice";
import AdvisoryDistanceError from "./AdvisoryDistanceError";


export const StrictModeDroppable = ( { children, ...props }: DroppableProps ) =>
{
    const [ enabled, setEnabled ] = useState( false );
    useEffect( () =>
    {
        const animation = requestAnimationFrame( () => setEnabled( true ) );
        return () =>
        {
            cancelAnimationFrame( animation );
            setEnabled( false );
        };
    }, [] );
    if ( !enabled )
    {
        return null;
    }
    return <Droppable {...props}>{children}</Droppable>;
};

type TripWaypointsFormProps = {
    formParent: UseFormReturn<RouteRequestFormData>,
    isAdvisory: boolean,
    backgroundColor: string,
};

function ResetStartAndEndPoint( formParent: UseFormReturn<RouteRequestFormData>, fieldsLength: number )
{
    formParent.setValue( "waypoints.0.overnightStop.minNights", 1 );
    formParent.setValue( "waypoints.0.overnightStop.maxNights", 1 );
    formParent.setValue( "waypoints.0.pitStop", undefined );
    formParent.setValue(
        `waypoints.${fieldsLength - 1}.overnightStop.minNights`,
        1
    );
    formParent.setValue(
        `waypoints.${fieldsLength - 1}.overnightStop.maxNights`,
        1
    );
    formParent.setValue(
        `waypoints.${fieldsLength - 1}.pitStop`,
        undefined
    );
}

export function TripWaypointsForm( {
    formParent,
    isAdvisory,
    backgroundColor
}: TripWaypointsFormProps )
{
    const { formState, register, control } = formParent;
    const dispatch = useDispatch();
    const { errors } = formState;
    const muiTextFieldRegister = useMuiFieldRegister( register, errors );
    const errorProps = useErrorProps( errors );
    const { fields, remove, move, insert } = useFieldArray( {
        control, // control props comes from useForm (optional: if you are using FormProvider)
        name: "waypoints", // unique name for your Field Array
    } );
    const [ isInteractingWithTextFieldLocationInput, setIsInteractingWithTextFieldLocationInput ] = useState<boolean>( false );
    useEffect( () =>
    {
        if ( errors.waypoints === undefined )
        {
            setIsInteractingWithTextFieldLocationInput( false );
        }
    }, [ errors ] );

    const userViewOverride = useUserViewOverride();
    const { isAdmin } = useIdentity( userViewOverride );


    const handleOnDragEnd = ( result: DropResult ) =>
    {
        if ( !result.destination ) return;
        const resetStops = result.source.index === 0 ||
            result.destination.index === 0 ||
            result.source.index === fields.length - 1 ||
            result.destination.index === fields.length - 1;
        move( result.source.index, result.destination.index );
        // when the rows move, reset these fields if either index is that start of end
        if ( resetStops )
        {
            ResetStartAndEndPoint( formParent, fields.length );

            // Must trigger revalidation
            void formParent.trigger();
        }

        // Dispatch the updated waypoints to the waypoints store
        dispatch( dragWaypoint( { sourceIndex: result.source.index, destinationIndex: result.destination.index } ) );
    };

    const handleRemove = ( index?: number | Array<number> ) =>
    {
        remove( index );
        void formParent.trigger();
        if ( errors.waypoints === undefined )
        {
            setIsInteractingWithTextFieldLocationInput( false );
        }
    };

    const forecastTime = useForecastTime();
    const [ isDateInFuture, setIsDateInFuture ] = useState( false );

    return (
        <Box
            sx = {{
                display: "grid",
                flexDirection: "column",
                gap: 3,
                alignItems: "start",
            }}
        >
            <Box sx = {{ display: "flex", alignItems: "center", gap: 2 }}>
                <TextField
                    label = {
                        <Tooltip title = {NAME_TOOLTIP}>
                            <Box>{NAME + "*"}</Box>
                        </Tooltip>
                    }
                    InputLabelProps = {{
                        shrink: true,
                    }}
                    sx = {{ width: "50%" }}
                    {...muiTextFieldRegister( "name" )}
                />
                <Controller
                    control = {control}
                    name = "startDate"
                    render = {( { field } ) => (
                        <DatePicker
                            ref = {field.ref}
                            disablePast = {!isAdmin}
                            minDate = {DateTime.fromISO( "1970-01-01" )}
                            label = {
                                <Tooltip title = {START_DATE_TOOLTIP}>
                                    <Box>{START_DATE + "*"}</Box>
                                </Tooltip>
                            }
                            value = {DateTime.fromJSDate( dateSchemaToDate( field.value ) )}
                            onChange = {( value: DateTime | null | undefined ) =>
                            {
                                if ( value === null || value === undefined )
                                {
                                    field.onChange( undefined );
                                }
                                else
                                {
                                    field.onChange( dateToSchema( value.toJSDate() ) );
                                    if ( forecastTime )
                                    {
                                        const forecastTimeStr = convertForecastTimeToDate( forecastTime );
                                        const forecastDateTime = new Date( forecastTimeStr );

                                        const futureDate = new Date( forecastDateTime );
                                        futureDate.setDate( forecastDateTime.getDate() + 8 );

                                        const selectedDate = value.toJSDate();

                                        const isFuture = selectedDate > futureDate;
                                        setIsDateInFuture( isFuture );
                                    }
                                }

                            }}
                            slotProps = {{
                                textField: {
                                    ...errorProps( field.name ),
                                    onBlur: field.onBlur,
                                },
                            }}
                            sx = {{ width: "50%" }}
                        />
                    )}
                />
            </Box>
            {isDateInFuture &&
            <Box>
                <span style = {{ color: "orange" }}>
                    {DATE_WARNING}
                </span>
            </Box>}
            <DragDropContext onDragEnd = {handleOnDragEnd}>
                <StrictModeDroppable droppableId = "waypoints">
                    {( provided ) => (
                        <div
                            className = "waypoints"
                            {...provided.droppableProps}
                            ref = {provided.innerRef}
                        >
                            {fields.map( ( field, index ) =>
                            {
                                const locationName = field.location.locationName;
                                return (
                                    <Draggable
                                        key = {field.id}
                                        draggableId = {field.id}
                                        index = {index}
                                        isDragDisabled = {isInteractingWithTextFieldLocationInput}
                                    >
                                        {( draggableProvided ) => (
                                            <div
                                                ref = {draggableProvided.innerRef}
                                                {...draggableProvided.draggableProps}
                                                {...draggableProvided.dragHandleProps}
                                            >
                                                <DraggableWaypoint
                                                    control = {control}
                                                    index = {index}
                                                    errorProp = {errorProps}
                                                    locationName = {locationName}
                                                    waypointSize = {fields.length}
                                                    formParent = {formParent}
                                                    isFirst = {index === 0}
                                                    isLast = {index === fields.length - 1}
                                                    muiTextFieldRegister = {muiTextFieldRegister}
                                                    handleRemove = {handleRemove}
                                                    setIsInteractingWithTextFieldLocationInput = {setIsInteractingWithTextFieldLocationInput}
                                                    backgroundColor = {backgroundColor}
                                                    isAdvisory = {isAdvisory}
                                                />
                                            </div>
                                        )}
                                    </Draggable>
                                );
                            } )}
                            {provided.placeholder}
                        </div>
                    )}
                </StrictModeDroppable>
                <Box sx = {{ display: "flex", justifyContent: "start", width: "100%" }}>
                    <IconButton
                        color = "primary"
                        disabled = {fields.length >= MAX_WAYPOINTS}
                        onClick = {() =>
                        {
                            insert( fields.length, defaultWaypoint );
                            dispatch( setSelectedWaypointInputIndex( fields.length ) );
                            dispatch( updateWaypoint( { index: fields.length, waypoint: defaultWaypoint, isDrag: false } ) );
                        }}
                        sx = {{
                            marginLeft: "6px",
                            marginBottom: "20px",
                            marginTop: "-30px",
                        }}
                    >
                        <AddCircleIcon/>
                    </IconButton>
                    <AdvisoryDistanceError isAdvisory = {isAdvisory}/>
                </Box>

                {( fields.length > 5 && !isAdvisory ) &&
                    <Alert severity = "warning">
                        <AlertTitle>Warning</AlertTitle>
                        {WAYPOINT_WARNING}
                    </Alert>}
            </DragDropContext>
            {formParent.getValues().waypoints.length >= 12 && (
                <span style = {{ color: "red" }}>
                    Warning: Trips longer than 30,000 miles may exceed the runtime limits.
                </span>
            )}
        </Box>
    );
}
